/** @jsxImportSource preact */
import { useEffect, useState } from 'preact/hooks'
import { callApi } from '../api'
import { FraudWaiverImport } from './FraudWaiverImport'
import { FraudWaiversTable, type Waiver } from './FraudWaiversTable'
import {
  changeFraudSettings,
  getFraudSettings,
  WAIVER_REASON_CODES,
  WAIVER_REASON_LABELS,
  type FraudSettings as Settings,
} from './fraud-settings-api'

/**
 * Everything that applies to fraud checking as a whole, kept away from the per-booking tab.
 *
 * The two switches and the waiver list are administration: they change what happens to every
 * customer with a prior default. Sitting them above a single booking's review made that review
 * harder to do and made the switches look like part of it.
 */

const daysSince = (iso: string) => Math.floor((Date.now() - new Date(iso).getTime()) / (24 * 60 * 60 * 1000))

export function FraudSettings() {
  // Three states, not two. A failed read must not render as "nothing is waived" or sit on
  // "loading" forever — on this screen an empty list is an assertion, and a spinner is a lie.
  const [settings, setSettings] = useState<Settings | null>(null)
  const [settingsFailed, setSettingsFailed] = useState(false)
  const [waivers, setWaivers] = useState<Waiver[] | null>(null)
  const [waiversFailed, setWaiversFailed] = useState(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState('')

  const [reference, setReference] = useState('')
  const [reasonCode, setReasonCode] = useState(WAIVER_REASON_CODES[0])
  const [note, setNote] = useState('')
  const [approver, setApprover] = useState('')

  const load = async () => {
    const [nextSettings, list] = await Promise.allSettled([
      getFraudSettings(),
      callApi<{ waivers: Waiver[] }>('/bookings/fraud/default-waiver', {}, 'Could not load waivers'),
    ])

    setSettingsFailed(nextSettings.status === 'rejected')
    if (nextSettings.status === 'fulfilled') setSettings(nextSettings.value)

    setWaiversFailed(list.status === 'rejected')
    if (list.status === 'fulfilled') setWaivers(list.value.waivers)
  }

  useEffect(() => {
    load()
  }, [])

  const change = async (changes: { enabled?: boolean; autoRejectEnabled?: boolean }) => {
    setBusy(true)
    setError('')
    try {
      setSettings(await changeFraudSettings(changes))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change the setting')
    } finally {
      setBusy(false)
    }
  }

  const waive = async () => {
    setBusy(true)
    setError('')
    setDone('')
    try {
      await callApi(
        '/bookings/fraud/default-waiver',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            bookingReference: reference.trim().toUpperCase(),
            reasonCode,
            reasonNote: note.trim(),
            approvedBy: approver.trim() || undefined,
          }),
        },
        'Failed to record the waiver',
      )
      setDone(`Waived ${reference.trim().toUpperCase()}`)
      setReference('')
      setNote('')
      setApprover('')
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to record the waiver')
    } finally {
      setBusy(false)
    }
  }

  const revoke = async (bookingReference: string) => {
    const reason = prompt(`Why is the waiver on ${bookingReference} being revoked?`)
    if (!reason?.trim()) return

    setBusy(true)
    setError('')
    try {
      await callApi(
        `/bookings/fraud/default-waiver/${bookingReference}`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: reason.trim() }),
        },
        'Failed to revoke the waiver',
      )
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to revoke the waiver')
    } finally {
      setBusy(false)
    }
  }

  const field =
    'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500'
  const label = 'block text-sm font-medium text-gray-700 mb-1'

  return (
    <div>
      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700">{error}</div>}

      <h2 className="text-lg font-semibold text-gray-800 mb-3">Automatic rejection</h2>
      {settingsFailed ? (
        <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-md text-sm text-red-800">
          <strong>Could not read the current settings.</strong> Nothing below reflects the live state — reload before
          changing anything.
        </div>
      ) : settings === null ? (
        <p className="mb-6 text-sm text-gray-500">Reading current settings…</p>
      ) : (
        <div className="mb-8 space-y-3">
          <div
            className={`p-4 border rounded-md text-sm flex flex-wrap items-center justify-between gap-3 ${
              settings.autoRejectEnabled
                ? 'bg-gray-50 border-gray-200 text-gray-700'
                : 'bg-red-50 border-red-300 text-red-900'
            }`}
          >
            <span>
              {settings.autoRejectEnabled ? (
                <>
                  <strong>Auto-reject is running.</strong> A booking with a confirmed related unpaid booking is
                  cancelled automatically
                  {settings.enabled ? ', unless that unpaid booking has an active waiver.' : '.'}
                </>
              ) : (
                <>
                  <strong>Auto-reject is paused.</strong> Bookings with a confirmed related unpaid booking are going to
                  manual review instead of being cancelled. Every one of them lands in the queue until it is switched
                  back on.
                </>
              )}
            </span>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                if (
                  settings.autoRejectEnabled &&
                  !confirm(
                    'Pause auto-reject? Bookings with a confirmed related unpaid booking will go to manual review instead of being cancelled.',
                  )
                ) {
                  return
                }
                change({ autoRejectEnabled: !settings.autoRejectEnabled })
              }}
              className={`px-3 py-1.5 rounded-md text-white disabled:bg-gray-300 ${
                settings.autoRejectEnabled ? 'bg-gray-600 hover:bg-gray-700' : 'bg-red-700 hover:bg-red-800'
              }`}
            >
              {settings.autoRejectEnabled ? 'Pause auto-reject' : 'Resume auto-reject'}
            </button>
          </div>

          <div
            className={`p-4 border rounded-md text-sm flex flex-wrap items-center justify-between gap-3 ${
              settings.enabled
                ? 'bg-green-50 border-green-200 text-green-900'
                : 'bg-amber-50 border-amber-200 text-amber-900'
            }`}
          >
            <span>
              {settings.enabled ? (
                <>
                  <strong>Waivers are active.</strong> A waived related unpaid booking sends the new booking to manual
                  review instead of cancelling it.
                </>
              ) : (
                <>
                  <strong>Waivers are not active.</strong> They are recorded and reviewable, but they change nothing
                  until this is switched on.
                </>
              )}
            </span>
            <button
              type="button"
              disabled={busy}
              onClick={() => change({ enabled: !settings.enabled })}
              className={`px-3 py-1.5 rounded-md text-white disabled:bg-gray-300 ${
                settings.enabled ? 'bg-gray-600 hover:bg-gray-700' : 'bg-green-700 hover:bg-green-800'
              }`}
            >
              {settings.enabled ? 'Switch off' : 'Switch on'}
            </button>
          </div>

          {settings.updatedBy && (
            <p className="text-xs text-gray-500">
              Last changed by {settings.updatedBy}
              {settings.updatedAt ? ` ${daysSince(settings.updatedAt)} days ago` : ''}.
            </p>
          )}
        </div>
      )}

      <h2 className="text-lg font-semibold text-gray-800 mb-1">Waive a related unpaid booking</h2>
      <p className="mb-3 text-sm text-gray-600">
        Use the unpaid booking's own reference &mdash; the one recorded as the DF Booking.{' '}
        {settings?.enabled === false
          ? 'It is recorded now and takes effect once waivers are switched on above.'
          : "The customer's next booking then comes here for review instead of being cancelled."}
      </p>

      {done && (
        <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-md text-green-800 text-sm">{done}</div>
      )}

      <div className="mb-8 p-4 border border-gray-200 rounded-md space-y-3">
        <div>
          <label
            htmlFor="fs-ref"
            className={label}
          >
            Unpaid booking reference <span className="text-red-500">*</span>
          </label>
          <input
            id="fs-ref"
            type="text"
            value={reference}
            placeholder="PLT1234567"
            onInput={e => setReference((e.target as HTMLInputElement).value)}
            className={`${field} font-mono`}
          />
        </div>
        <div>
          <label
            htmlFor="fs-code"
            className={label}
          >
            Reason
          </label>
          <select
            id="fs-code"
            value={reasonCode}
            onChange={e => setReasonCode((e.target as HTMLSelectElement).value)}
            className={field}
          >
            {WAIVER_REASON_CODES.map(code => (
              <option
                key={code}
                value={code}
              >
                {WAIVER_REASON_LABELS[code]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label
            htmlFor="fs-note"
            className={label}
          >
            Note <span className="text-red-500">* why this booking is being waived</span>
          </label>
          <input
            id="fs-note"
            type="text"
            value={note}
            onInput={e => setNote((e.target as HTMLInputElement).value)}
            className={field}
          />
        </div>
        <div>
          <label
            htmlFor="fs-approver"
            className={label}
          >
            Approved by{' '}
            <span className="text-gray-500">
              (required if anything is still owed or the balance is not verified, and not you)
            </span>
          </label>
          <input
            id="fs-approver"
            type="text"
            value={approver}
            onInput={e => setApprover((e.target as HTMLInputElement).value)}
            className={field}
          />
        </div>
        <button
          type="button"
          disabled={busy || !reference.trim() || !note.trim()}
          onClick={waive}
          className="px-4 py-2 bg-indigo-600 text-white rounded-md disabled:bg-gray-300"
        >
          {busy ? 'Saving…' : 'Waive this booking'}
        </button>
      </div>

      <h2 className="text-lg font-semibold text-gray-800 mb-3">Active waivers</h2>
      {waiversFailed ? (
        <p className="text-sm text-red-700">
          <strong>Could not load the waivers.</strong> This is not the same as there being none.
        </p>
      ) : waivers === null ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : waivers.length === 0 ? (
        <p className="text-sm text-gray-500">No waivers are currently in place.</p>
      ) : (
        <FraudWaiversTable
          waivers={waivers}
          switchedOff={settings?.enabled === false}
          busy={busy}
          onRevoke={revoke}
        />
      )}

      <FraudWaiverImport onImported={load} />
    </div>
  )
}
